import { cn } from '@/lib/utils';
import { ConversationListItem } from '@/types';
import { motion } from 'framer-motion';
import { ChatIcon, EditIcon, DeleteIcon } from '@/components/common/icons/SVGs';
import { Textarea } from '@/components/common/inputs/textarea/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/common/overlay/dialog/dialog';
import { Button } from '@/components/common/inputs/button/button';
import React, { useCallback, useState } from 'react';
import { observer, use$ } from '@legendapp/state/react';
import { batch } from '@legendapp/state';
import { adviceState, adviceActions } from '@/lib/store/advice.store';
import { logger } from '@/lib/utils/logger';
import { useToast } from '@/lib/hooks/useToast';
import { adviceService } from '@/lib/services/advice.service';
import { useI18n } from '@/locales/client';
import { sidebarState } from '@/lib/store/sidebar.store';

const itemVariants = {
  hidden: { opacity: 0, x: -20, scale: 0.95 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 500, damping: 30 },
  },
  exit: { opacity: 0, x: -20, scale: 0.95, transition: { duration: 0.2 } },
};

const buttonVariants = {
  rest: { scale: 1 },
  hover: { scale: 1.1 },
  tap: { scale: 0.95 },
};

interface ChatItemProps {
  chat: ConversationListItem;
  currentChat: string | null;
  onNavigate: (path: string) => void;
  toast: ReturnType<typeof useToast>['toast'];
}

const ChatItemComponent = React.forwardRef<HTMLDivElement, ChatItemProps>(
  (props, ref) => {
    const { chat, currentChat, onNavigate, toast } = props;
    const t = useI18n();
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [editTitle, setEditTitle] = useState(chat.title);
    const [isDeleting, setIsDeleting] = useState(false);

    // Subscribe directly to individual store values using use$
    const status = use$(adviceState.status);
    const isTransitioning = use$(adviceState.isTransitioning);
    const currentChatId = use$(adviceState.currentChatId);
    const activeConversation = use$(adviceState.activeConversation);
    const sidebarIsDeleting = use$(sidebarState.isDeleting);
    const sidebarStatus = use$(sidebarState.status);
    const activeController = use$(adviceState.activeController);

    // Determine if this chat is active/pending.
    const isEditing = currentChat === chat.id;
    const isActive = !isTransitioning && currentChatId === chat.id;
    const isPending = status === 'submitted';

    const handleEditClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setEditTitle(chat.title);
        setIsEditDialogOpen(true);
      },
      [chat.title]
    );

    const handleSaveEdit = useCallback(async () => {
      if (!editTitle.trim()) return;
      try {
        await adviceActions.updateTitle(chat.id, editTitle);
        setIsEditDialogOpen(false);
      } catch (error) {
        logger.error('[ChatItem] Failed to save edit:', { id: chat.id, error });
        toast({
          variant: 'destructive',
          description: t('chat.edit.error'),
        });
      }
    }, [chat.id, editTitle, toast, t]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          handleSaveEdit();
        }
      },
      [handleSaveEdit]
    );

    const handleClick = useCallback(() => {
      if (
        isActive ||
        status === 'submitted' ||
        isTransitioning ||
        sidebarIsDeleting
      )
        return;
      onNavigate(`${chat.id}`);
    }, [
      chat.id,
      isActive,
      onNavigate,
      status,
      isTransitioning,
      sidebarIsDeleting,
    ]);

    const handleDeleteClick = useCallback(async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
      if (sidebarIsDeleting || sidebarStatus === 'pending' || isDeleting) {
        logger.debug('[ChatItem] Delete already in progress');
        return;
      }
      setIsDeleting(true);
      try {
        await adviceActions.deleteChat(chat.id);
        toast({ description: t('chat.delete.success') });
      } catch (error) {
        logger.error('[ChatItem] Delete failed:', { id: chat.id, error });
      } finally {
        setIsDeleting(false);
        setIsDeleteDialogOpen(false);
      }
    }, [chat.id, isDeleting, sidebarIsDeleting, sidebarStatus, toast, t]);

    return (
      <>
        <motion.div
          ref={ref}
          variants={itemVariants}
          initial='hidden'
          animate='visible'
          exit='exit'
          layout
          onClick={handleClick}
          className={cn(
            'relative flex h-[60px] cursor-pointer select-none items-center space-x-3 border-b border-gray-200 dark:border-gray-700 px-3 shadow-sm shadow-purple-200 dark:shadow-none transition-colors',
            isActive ? 'bg-purple-100 dark:bg-purple-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800/70',
            (isPending || isTransitioning) && 'cursor-not-allowed opacity-50'
          )}
          whileHover={{
            scale: isPending || isTransitioning ? 1 : 1.01,
            transition: { duration: 0.2 },
          }}
          whileTap={{
            scale: isPending || isTransitioning ? 1 : 0.99,
          }}
        >
          <span className='flex-shrink-0 text-gray-600 dark:text-gray-400'>
            <ChatIcon />
          </span>
          <span className='line-clamp-2 min-w-0 flex-1 pr-14 text-left text-sm font-medium leading-tight text-gray-800 dark:text-gray-100'>
            {chat.title}
          </span>
          <motion.div
            layout
            className='absolute right-2 flex h-full items-center space-x-1 px-0'
          >
            <div className='flex items-center gap-1'>
              <motion.button
                variants={buttonVariants}
                initial='rest'
                whileHover='hover'
                whileTap='tap'
                onClick={handleEditClick}
                className='rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700'
                aria-label='Edit chat title'
              >
                <EditIcon className='h-4 w-4 text-gray-600 dark:text-gray-400' />
              </motion.button>
              <motion.button
                variants={buttonVariants}
                initial='rest'
                whileHover='hover'
                whileTap='tap'
                onClick={handleDeleteClick}
                className='rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700'
                aria-label='Delete chat'
              >
                <DeleteIcon className='h-4 w-4 text-gray-600 dark:text-gray-400' />
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className='sm:max-w-[425px] dark:bg-gray-900/80 dark:border-opacity-30'>
            <DialogHeader>
              <DialogTitle className='dark:text-gray-100'>{t('chat.delete.confirmTitle')}</DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-300 pt-2">
                {t('chat.delete.confirmDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className='flex justify-end space-x-2 pt-2'>
              <Button
                variant='outline'
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeleting}
                className='dark:border-opacity-30 dark:bg-gray-900/30 dark:text-gray-100 dark:hover:bg-gray-800'
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant='destructive'
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className='dark:bg-red-900/80 dark:hover:bg-red-900'
              >
                {isDeleting ? (
                  <div className='flex items-center'>
                    <span className='mr-2'>{t('chat.delete.deleting')}</span>
                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
                  </div>
                ) : (
                  t('chat.delete.confirm')
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Chat Title Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className='sm:max-w-[425px] dark:bg-gray-900/80 dark:border-opacity-30'>
            <DialogHeader>
              <DialogTitle className='dark:text-gray-100'>{t('chat.edit.title')}</DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-300">
                {t('chat.edit.placeholder')}
              </DialogDescription>
            </DialogHeader>
            <div className='py-4'>
              <Textarea
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className='min-h-[100px] resize-none dark:bg-gray-900/30 dark:border-opacity-30 dark:text-gray-100'
                placeholder={t('chat.edit.placeholder')}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
                {t('chat.edit.hint')}
              </p>
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setIsEditDialogOpen(false)}
                className='dark:border-opacity-30 dark:bg-gray-900/30 dark:text-gray-100 dark:hover:bg-gray-800'
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSaveEdit} className='dark:bg-purple-700 dark:hover:bg-purple-600'>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
);

ChatItemComponent.displayName = 'ChatItem';
export const ChatItem = observer(ChatItemComponent);
